import React, { useState, useRef, useEffect } from 'react';
import { useConversations } from '../hooks/useConversations';
import { useChatClient, useChatData } from '../context/ChatContext';
import ConversationItem from './ConversationItem';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faPlus, faUsers, faGear, faRotate } from '@fortawesome/free-solid-svg-icons';

interface ConversationListProps {
  onNewChat: () => void;
  onNewGroup: () => void;
  onSelectConversation: (conversationId: string) => void;
}

const ConversationList: React.FC<ConversationListProps> = ({ onNewChat, onNewGroup, onSelectConversation }) => {
  const { t } = useTranslation();
  const {
    conversations,
    isSyncing,
    unreadCounts,
    searchQuery,
    conversationFilter,
    setSearchQuery,
    setFilter,
    syncConversations,
  } = useConversations();
  const { activeConversationId } = useChatData();
  const { resetChat } = useChatClient();
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const settingsMenuRef = useRef<HTMLDivElement>(null);

  // Close settings menu when clicking outside
  useEffect(() => {
    if (!showSettingsMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(e.target as Node)) {
        setShowSettingsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSettingsMenu]);

  return (
    <div className="chat-conversation-list">
      {/* Header */}
      <div className="chat-conversation-list__header">
        <h5 className="chat-conversation-list__title">
          {t('chat.messages', 'Messages')}
        </h5>
        <div className="chat-conversation-list__actions">
          <button
            className="chat-btn chat-btn--icon"
            onClick={syncConversations}
            disabled={isSyncing}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faArrowsRotate} spin={isSyncing} />
          </button>
          <button
            className="chat-btn chat-btn--icon"
            onClick={onNewChat}
            title={t('chat.newConversation', 'New Chat')}
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
          <button
            className="chat-btn chat-btn--icon"
            onClick={onNewGroup}
            title={t('chat.newGroup', 'New Group')}
          >
            <FontAwesomeIcon icon={faUsers} />
          </button>
          <div ref={settingsMenuRef} style={{ position: 'relative' }}>
            <button
              className="chat-btn chat-btn--icon"
              onClick={() => setShowSettingsMenu(!showSettingsMenu)}
              title={t('chat.settings', 'Settings')}
            >
              <FontAwesomeIcon icon={faGear} />
            </button>
            {showSettingsMenu && (
              <div className="chat-settings-dropdown">
                <button
                  className="chat-settings-dropdown__item chat-settings-dropdown__item--danger"
                  onClick={() => {
                    setShowSettingsMenu(false);
                    if (window.confirm('This will reset your chat identity and create a new one. Your existing conversations will be re-synced from the network. Continue?')) {
                      resetChat();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faRotate} />
                  <span>{t('chat.resetIdentity', 'Reset Chat Identity')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="chat-conversation-list__search">
        <input
          type="text"
          className="chat-search-input"
          placeholder={t('chat.search', 'Search conversations...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Filter tabs */}
      <div className="chat-conversation-list__filters">
        {(['all', 'dms', 'groups'] as const).map((f) => (
          <button
            key={f}
            className={`chat-filter-btn ${conversationFilter === f ? 'chat-filter-btn--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? t('chat.all', 'All') : f === 'dms' ? t('chat.dms', 'DMs') : t('chat.groups', 'Groups')}
          </button>
        ))}
      </div>

      {/* Conversation items */}
      <div className="chat-conversation-list__items">
        {conversations.length === 0 ? (
          <div className="chat-conversation-list__empty">
            <p>{t('chat.noConversations', 'No conversations yet')}</p>
            <button className="chat-btn chat-btn--primary" onClick={onNewChat}>
              {t('chat.startChat', 'Start a Chat')}
            </button>
          </div>
        ) : (
          conversations.map((convo) => (
            <ConversationItem
              key={convo.id}
              conversation={convo}
              isActive={activeConversationId === convo.id}
              unreadCount={unreadCounts[convo.id] || 0}
              onClick={() => onSelectConversation(convo.id)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationList;
